<script setup>
import { ref, computed, watch } from 'vue'
import StreamPlayer from '@/components/ui/StreamPlayer.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconWirelessSignal from '@/components/icon/IconWirelessSignal.vue'
import IconLinkChain from '@/components/icon/IconLinkChain.vue'
import IconLock from '@/components/icon/IconLock.vue'
import IconCloud from '@/components/icon/IconCloud.vue'
import IconOpenExternal from '@/components/icon/IconOpenExternal.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import MediaflowMonitorHeader from './MediaflowMonitorHeader.vue'
import MediaflowSceneCanvas from './MediaflowSceneCanvas.vue'
import { cueRemainingSec, formatCountdown } from './mediaflowMeta'

/**
 * The Program monitor, two views:
 * - Scene (default): the on-air scene and its live graphics drawn in the
 *   browser with the server's geometry. Works offline, follows every switch,
 *   take and cue at once.
 * - Output: the server's return feed (`<key>_preview`, the same encode the
 *   destination gets), i.e. exactly what viewers receive. Live only.
 * The choice is remembered per production.
 */
const props = defineProps({
	label: { type: String, default: 'Program' },
	mediaflow: { type: Object, required: true },
	destinationStream: { type: Object, default: null },
	/** A destination is set but the streams list is still loading. */
	destinationPending: { type: Boolean, default: false },
	mediaPulse: { type: Object, default: null },
	portrait: { type: Boolean, default: false },
	/** A session is running: an offline monitor is "starting" rather than offline. */
	sessionActive: { type: Boolean, default: false },
	/** From `outputHealth()`: what the server sees on this output. */
	health: { type: Object, default: null },
	/** The flow currently on air (name shown in the header). */
	onAirFlow: { type: Object, default: null },
	/** Running break cue `{ flowId, returnsAt, returnFlowId, overlays }` or null. */
	cue: { type: Object, default: null },
	/** Wall clock tick for the countdown. */
	now: { type: Number, default: () => Date.now() },
	/** Flow lookup for the cue labels. */
	flows: { type: Array, default: () => [] },
	ending: { type: Boolean, default: false },
	/** Scene view: canvas size, streams for live layers, production graphics. */
	canvas: { type: Object, default: () => ({ width: 1920, height: 1080 }) },
	streams: { type: Array, default: () => [] },
	overlays: { type: Array, default: () => [] },
	/** Hide the status strip (compact pop-out). */
	strip: { type: Boolean, default: true },
})

const emit = defineEmits(['end-break'])

const cueRemaining = computed(() => cueRemainingSec(props.cue, props.now))
const cueFlow = computed(() => props.flows.find((f) => f._id === props.cue?.flowId) || null)
const cueReturn = computed(() => props.flows.find((f) => f._id === props.cue?.returnFlowId) || null)

const healthClass = computed(() => ({
	success: 'text-success',
	warning: 'text-gold',
	muted: 'text-surface-8',
}[props.health?.tone || 'muted']))
const healthDot = computed(() => ({
	success: 'bg-success',
	warning: 'bg-gold animate-pulse',
}[props.health?.tone] || 'bg-surface-6'))

const alive = computed(() => !!props.mediaPulse?.alive)
const enabled = computed(() => !!props.mediaflow?.enabled)
const hasDestination = computed(() => !!props.destinationStream)

// ---------------------------------------------------------------- view
const VIEWS = [{ key: 'scene', label: 'Scene' }, { key: 'output', label: 'Output' }]
const storageKey = computed(() => `castr-mf-program-view:${props.mediaflow?._id || ''}`)
const readView = () => {
	try { return localStorage.getItem(storageKey.value) === 'output' ? 'output' : 'scene' } catch (e) { return 'scene' }
}
const view = ref(readView())
watch(storageKey, () => { view.value = readView() })
const setView = (v) => {
	view.value = v
	try { localStorage.setItem(storageKey.value, v) } catch (e) { /* private mode */ }
}

/** Graphics on Program: the live ones, unless the running break hides them. */
const programOverlays = computed(() => {
	if (props.cue?.overlays === 'hide') return []
	return props.overlays.filter((o) => o && o.live)
})

// ---------------------------------------------------------------- output view
const streamPageUrl = computed(() => (props.destinationStream?._id ? `/app/livestreams/${props.destinationStream._id}` : ''))
const playerStream = computed(() => ({ ...props.mediaflow, key: `${props.mediaflow.key}_preview` }))

const state = computed(() => {
	if (!enabled.value) return 'disabled'
	if (!hasDestination.value && !props.destinationPending) return 'no-destination'
	if (alive.value) return 'playing'
	if (props.sessionActive) return 'starting'
	return 'offline'
})

const empty = computed(() => ({
	disabled: { icon: 'lock', title: 'Production is disabled', hint: '' },
	'no-destination': { icon: 'link', title: 'No destination yet', hint: 'Choose one in Settings.' },
	starting: props.health?.key === 'reconnecting'
		? { icon: 'signal', title: 'Reconnecting…', hint: '', pulse: true }
		: { icon: 'signal', title: 'Starting…', hint: 'Usually under a minute.', pulse: true },
	offline: { icon: 'signal', title: 'Offline', hint: 'Go live to see the output stream.' },
}[state.value] || null))
</script>

<template>
	<!-- lg+: `display: contents`, rows 1–3 of column 3 in the parent's monitor grid (see MediaflowEditorPanel). -->
	<div
		class="flex min-w-0 flex-col gap-2 lg:contents"
		:aria-label="`${label} monitor`"
		data-monitor="live"
		:data-state="state"
		:data-program-view="view"
		:data-cue="cue ? 'running' : undefined"
	>
		<!-- Header: one red tally, the Scene | Output switch, and a running break's countdown + End now -->
		<MediaflowMonitorHeader class="lg:col-start-3 lg:row-start-1" :label="label" tone="program" testid="program" :name="onAirFlow?.name || ''" :pulse="alive">
			<template #actions>
				<span v-if="cue" class="flex shrink-0 items-center gap-2" data-testid="program-header-break">
					<span class="inline-flex h-[22px] items-center gap-1 rounded bg-error px-1.5 text-[0.65rem] font-semibold leading-none tracking-wider text-white">
						BREAK<span v-if="cueRemaining !== null" class="font-mono tabular-nums" data-testid="program-break-countdown">{{ formatCountdown(cueRemaining) }}</span>
					</span>
					<FormButton type="red" size="xs" class="!px-2 font-semibold" :is-loading="ending" data-testid="program-header-end" @click="emit('end-break')">End now</FormButton>
				</span>
				<div class="grid h-6 shrink-0 grid-cols-2 gap-0.5 rounded-md bg-surface-1 p-0.5 ring-1 ring-surface-3" role="radiogroup" aria-label="Program view" data-testid="program-view">
					<button
						v-for="v in VIEWS"
						:key="v.key"
						type="button"
						role="radio"
						:aria-checked="view === v.key"
						class="inline-flex min-w-0 items-center justify-center gap-1 rounded px-2 text-[0.7rem] font-medium leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
						:class="view === v.key ? 'bg-surface-4 text-white' : 'text-surface-7 hover:text-surface-text-2'"
						:title="v.key === 'scene' ? 'The on-air scene, drawn here' : 'The stream viewers get'"
						:data-testid="`program-view-${v.key}`"
						@click="setView(v.key)"
					>
						<span v-if="v.key === 'output' && sessionActive" class="size-1.5 shrink-0 rounded-full" :class="healthDot" aria-hidden="true" data-testid="program-view-health" />
						{{ v.label }}
					</button>
				</div>
			</template>
		</MediaflowMonitorHeader>

		<!-- Video -->
		<div class="min-w-0 lg:col-start-3 lg:row-start-2" :class="portrait ? 'mx-auto w-full max-w-[min(100%,calc(60vh*9/16))]' : ''">
			<div class="relative isolate w-full">
				<MediaflowSceneCanvas
					v-if="view === 'scene'"
					:class="alive ? '!ring-error' : ''"
					:flow="onAirFlow"
					:canvas="canvas"
					:streams="streams"
					:overlays="programOverlays"
					:editable="false"
					:interactive="false"
					:on-air="sessionActive"
					placeholder="Nothing on Program yet."
					data-testid="program-scene"
				/>
				<div
					v-else
					class="relative w-full overflow-hidden bg-black ring-1"
					:class="[portrait ? 'aspect-[9/16]' : 'aspect-video', alive ? 'ring-error' : 'ring-surface-3']"
					data-testid="program-output"
				>
					<div class="absolute inset-0 flex items-center justify-center">
						<StreamPlayer
							v-if="state === 'playing'"
							:obs="true"
							:mediaPulse="mediaPulse"
							:stream="playerStream"
							:streamUrl="''"
							:multiview="false"
							:refLink="1"
							:isPreview="false"
							:portrait="portrait"
						/>
						<div v-else-if="empty" class="relative z-[1] flex flex-col items-center justify-center gap-1 px-6 py-4 text-center" :aria-busy="state === 'starting' || undefined">
							<span class="mb-1.5 inline-flex size-10 items-center justify-center rounded-full bg-surface-2" :class="empty.pulse ? 'text-main-light animate-pulse' : 'text-surface-8'">
								<icon-base v-if="empty.icon === 'lock'" width="18" height="18" fill="none"><icon-lock /></icon-base>
								<icon-base v-else-if="empty.icon === 'link'" width="16" height="16" :width-view-box="14" :height-view-box="14" fill="none"><icon-link-chain /></icon-base>
								<icon-base v-else width="22" height="22" :width-view-box="24" :height-view-box="24" fill="none"><icon-wireless-signal /></icon-base>
							</span>
							<p class="m-0 text-tiny font-semibold text-surface-text-2">{{ empty.title }}</p>
							<p v-if="empty.hint" class="m-0 max-w-[260px] text-xs leading-snug text-surface-8">{{ empty.hint }}</p>
						</div>
					</div>
					<div v-if="state !== 'playing'" class="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:10%_17.78%] [mask-image:radial-gradient(ellipse_at_center,rgba(0,0,0,0.9),transparent_75%)]" aria-hidden="true" />
				</div>

				<!-- Break running: what plays and where it returns. z above the player and canvas overlays. -->
				<div v-if="cue" class="absolute inset-x-2 bottom-2 z-[1004] flex items-center gap-2 rounded-lg bg-black/70 px-3 py-2 ring-1 ring-error backdrop-blur" role="status" aria-live="polite" data-testid="program-break">
					<span class="inline-flex items-center gap-1 rounded bg-error px-1.5 py-0.5 text-[0.65rem] font-semibold tracking-wider text-white">
						<span class="size-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />BREAK
					</span>
					<span class="min-w-0 flex-1 truncate text-xs text-white">
						<span class="font-medium">{{ cueFlow?.name || 'Break' }}</span>
						<span class="text-surface-8"> → back to {{ cueReturn?.name || 'the previous scene' }}</span>
					</span>
				</div>
			</div>
		</div>

		<!-- Status strip under the video, mirroring the Preview toolbar -->
		<div v-if="strip" class="flex h-9 min-w-0 items-center gap-2 overflow-hidden rounded-lg bg-surface-2 px-3 text-xs ring-1 ring-surface-3 lg:col-start-3 lg:row-start-3" data-testid="program-strip">
			<template v-if="health && (alive || sessionActive)">
				<span class="inline-flex min-w-0 items-center gap-1.5" :class="healthClass" :data-health="health.key">
					<span class="size-1.5 shrink-0 rounded-full" :class="healthDot" aria-hidden="true" />
					<span class="truncate font-medium">{{ health.label }}</span>
				</span>
				<span v-if="health.details.length" class="hidden min-w-0 truncate text-surface-8 md:inline">· {{ health.details.join(' · ') }}</span>
			</template>
			<span v-else class="flex min-w-0 items-center gap-1.5 text-surface-8">
				<span class="shrink-0">What viewers see<template v-if="destinationStream"> on</template></span>
				<Tooltip v-if="destinationStream" content="Open stream page" wrapper-class="inline-flex min-w-0">
					<a
						:href="streamPageUrl"
						target="_blank"
						rel="noopener"
						class="inline-flex min-w-0 max-w-[220px] items-center gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 font-medium text-surface-text-2 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
						:aria-label="`Open ${destinationStream.name} stream page in a new tab`"
						data-testid="program-stream-link"
					>
						<span class="truncate">{{ destinationStream.name }}</span>
						<icon-base class="shrink-0 text-surface-8" width="11" height="11"><icon-open-external /></icon-base>
					</a>
				</Tooltip>
			</span>
			<span v-if="view === 'output'" class="ml-auto hidden shrink-0 items-center gap-1 text-surface-7 xl:inline-flex" title="The server's own return feed of the output">
				<icon-base width="12" height="12" :width-view-box="16" :height-view-box="16" fill="none"><icon-cloud /></icon-base>
				Server feed
			</span>
		</div>
	</div>
</template>
